import { ServicoDisponivelEnum, obterServicoDisponivelPorCodigo } from '@/features/servicos/shared/enums/servico-disponivel.enum';

/**
 * A regra da inscrição ex officio do pré-prestador, num lugar só.
 *
 * Três pontos perguntam a mesma coisa — o filtro do quadro, o botão "Efetivar Solicitação" e o
 * arraste para Concluídos. Se cada um decidisse por conta, um gesto passaria a aceitar o que o outro
 * recusa, e a diferença entre eles seria justamente inscrever uma empresa sem pagamento.
 *
 * Aqui é antecipação de resposta, não autorização: quem decide de fato é
 * `PrePrestadorService.efetivarSolicitacaoExOfficio`, que reconfere prazo e pendência contra o
 * relógio do servidor. A tela erra para menos — na dúvida, manda pelo fluxo comum e deixa o backend
 * recusar.
 */

/** Dias de inércia a partir dos quais o Conselho pode inscrever de ofício. Espelha o backend. */
export const DIAS_PARA_EX_OFFICIO = 30;

/**
 * O que a regra precisa saber do protocolo — e nada além disso.
 *
 * Estrutural de propósito: a MESMA decisão é tomada sobre `ItemStatus` (quadro, filtro) e sobre
 * `ProtocoloResumo` (card de detalhe), que são vistas diferentes do mesmo protocolo. Amarrar a regra
 * a um dos dois obrigaria o outro a fabricar um objeto falso só para perguntar — e é aí que os dois
 * gestos começam a responder coisas diferentes.
 */
export interface DadosDoExOfficio {
    dataSolicitacao?: string;
    inPago?: boolean;
    isAnuidadePaga?: boolean;
}

/** Uma data no calendário, sem hora e sem fuso — a unidade em que o prazo de inércia é contado. */
interface DataCivil {
    ano: number;
    mes: number;
    dia: number;
}

const MILISSEGUNDOS_POR_DIA = 86_400_000;

/** `dd/MM/yyyy`, `yyyy-MM-dd` e ISO com hora: os três formatos em que o portal manda `dataHora`. */
const FORMATO_BR = /^(\d{2})\/(\d{2})\/(\d{4})$/;
const FORMATO_ISO = /^(\d{4})-(\d{2})-(\d{2})(?:[T ].*)?$/;

/**
 * Reconhece os formatos do portal em vez de entregar o texto ao `new Date`.
 *
 * O parser do JS lê `dd/MM/yyyy` como se fosse `MM/dd/yyyy`: `05/08/2026` (5 de agosto) virava 8 de
 * maio e inflava a inércia em três meses, e `25/07/2026` virava `Invalid Date`. O mesmo campo, no
 * mesmo formato, respondia uma coisa ou outra conforme o dia do mês fosse maior que 12 — e no lado
 * que errava para mais a tela oferecia e NOMEAVA uma inscrição de ofício sobre empresa de duas
 * semanas, para o servidor recusar depois com um 409 que o analista não tem como interpretar.
 *
 * O conjunto de formatos é o mesmo do `DataPortalUtils` do backend, de propósito: divergir aqui é
 * como o desacordo volta.
 */
function dataCivil(valor: string): DataCivil | null {
    const texto = valor.trim();

    const br = FORMATO_BR.exec(texto);
    if (br) {
        return civilValida(Number(br[3]), Number(br[2]), Number(br[1]));
    }

    const iso = FORMATO_ISO.exec(texto);
    if (iso) {
        // A parte de data do texto, e não o instante convertido para o fuso local: é o que
        // `OffsetDateTime.parse(...).toLocalDate()` faz do outro lado.
        return civilValida(Number(iso[1]), Number(iso[2]), Number(iso[3]));
    }

    return null;
}

/** Recusa combinações que não existem no calendário (31/02, mês 13), que o `Date` normalizaria em silêncio. */
function civilValida(ano: number, mes: number, dia: number): DataCivil | null {
    const instante = new Date(Date.UTC(ano, mes - 1, dia));
    const existe = instante.getUTCFullYear() === ano && instante.getUTCMonth() === mes - 1 && instante.getUTCDate() === dia;

    return existe ? { ano, mes, dia } : null;
}

/** Dias de calendário decorridos, contados como o backend conta: entre duas datas, sem hora no meio. */
function diferencaEmDias(de: DataCivil, ate: DataCivil): number {
    const inicio = Date.UTC(de.ano, de.mes - 1, de.dia);
    const fim = Date.UTC(ate.ano, ate.mes - 1, ate.dia);

    return Math.round((fim - inicio) / MILISSEGUNDOS_POR_DIA);
}

/**
 * Dias inteiros entre a data informada e agora. `null` quando não há data legível.
 *
 * A conta é de dias de CALENDÁRIO, espelhando o `ChronoUnit.DAYS.between(abertura, hoje)` do
 * servidor. Subtrair instantes fazia o resultado depender da hora — meia-noite UTC lida em UTC-3
 * adiantava a conta em um dia, e o vigésimo nono dia se apresentava como o trigésimo bem na fronteira
 * que decide o ato.
 */
export function diasDesde(valor: string | undefined, agora: number): number | null {
    if (!valor) {
        return null;
    }

    const abertura = dataCivil(valor);
    if (!abertura) {
        return null;
    }

    const referencia = new Date(agora);
    const hoje: DataCivil = { ano: referencia.getFullYear(), mes: referencia.getMonth() + 1, dia: referencia.getDate() };

    return diferencaEmDias(abertura, hoje);
}

/**
 * A empresa está em aberto — protocolo não pago OU anuidade pendente.
 *
 * É o complemento exato de "dá para efetivar pelo fluxo comum": qualquer uma das duas em falta já
 * caracteriza pendência financeira.
 */
export function temPendenciaFinanceira(item: DadosDoExOfficio): boolean {
    return !(item.inPago === true && item.isAnuidadePaga !== false);
}

/**
 * A empresa está apta à inscrição ex officio.
 *
 * @param agora referência de "hoje" em milissegundos; recebida de fora para o teste não depender do relógio.
 */
export function aptoAExOfficio(item: DadosDoExOfficio, agora: number): boolean {
    const dias = diasDesde(item.dataSolicitacao, agora);
    if (dias === null || dias < DIAS_PARA_EX_OFFICIO) {
        return false;
    }

    return temPendenciaFinanceira(item);
}

/**
 * Texto da confirmação, quando o gesto comum de efetivar vai praticar um ato de ofício.
 *
 * O analista clicou no botão de sempre; é aqui que ele descobre que o efeito é outro — empresa
 * inscrita sem pagar, CRM provisório e um prazo correndo. Some com este texto e o ato passa a
 * acontecer sem que ninguém tenha dito que sim a ele.
 */
export function mensagemConfirmacaoExOfficio(item: DadosDoExOfficio, agora: number): string {
    const dias = diasDesde(item.dataSolicitacao, agora);
    const inercia = dias === null ? 'mais de 30 dias' : `${dias} dias`;

    return (
        `Esta empresa está há ${inercia} sem concluir o registro e com pendência financeira. ` +
        'Ela será INSCRITA DE OFÍCIO, sem pagamento: o CRM nasce na situação PROVISÓRIA EX OFFICIO ' +
        'e a empresa passa a ter 30 dias para regularizar. Confirma a inscrição ex officio?'
    );
}

/**
 * O serviço tem inscrição ex officio.
 *
 * Só o pré-prestador tem: é lá que o Conselho inscreve a empresa por decurso de prazo. Pelo id do
 * catálogo, e não pelo texto do código, porque é o id que identifica o serviço — o código é apelido
 * de rota e já tem variantes em outros serviços.
 */
export function servicoTemExOfficio(codigoServico: string): boolean {
    return obterServicoDisponivelPorCodigo(codigoServico)?.id === ServicoDisponivelEnum.PRE_PRESTADOR;
}

/**
 * O gesto comum de efetivar VAI praticar o ato de ofício — a pergunta que o botão, o arraste e a
 * confirmação fazem antes de agir.
 *
 * As quatro condições são cumulativas e nenhuma delas é dispensável:
 *
 * - `inPago !== true`: o ex officio é EXCEÇÃO ao gate de pagamento, então só existe onde o gate
 *   barra. Com o protocolo quitado o fluxo comum já resolve — e o backend recusa (409) justamente
 *   um ex officio sobre protocolo quite. Esta linha é o que garante que nada muda no caminho normal;
 * - `servicoTemExOfficio`: nos demais serviços o ato não existe;
 * - `aptoAExOfficio`: decurso de prazo e pendência financeira, a regra de sempre;
 * - `temRoleExOfficio`: quem só tem `confirmar` NÃO ganha o poder de inscrever sem pagamento. Sem a
 *   role a tela se comporta como antes (ação barrada), em vez de habilitar o botão para o servidor
 *   devolver 403 depois — recusa que chegaria como falha genérica, sem dizer que faltava permissão.
 *
 * Quem consulta o `PermissionService` é o componente (ele conhece a UF do protocolo); aqui entra só
 * a resposta, para a regra continuar pura e testável sem TestBed. E entra como função, avaliada por
 * último: a pergunta de permissão é a única com custo e a única observável de fora, e um serviço que
 * nem tem ex officio não pode passar a consultar roles só porque este teste existe.
 */
export function efetivacaoSeraExOfficio(codigoServico: string, dados: DadosDoExOfficio | null | undefined, agora: number, temRoleExOfficio: () => boolean): boolean {
    if (!dados || dados.inPago === true) {
        return false;
    }

    if (!servicoTemExOfficio(codigoServico) || !aptoAExOfficio(dados, agora)) {
        return false;
    }

    return temRoleExOfficio();
}
